import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import type {
  ChatCompletionsMessageObject,
  TChatCompletionRequest,
} from "../../../services/api/instances/completionsApi/types";
import type { DialogValue } from "../../../services/api/instances/dialogApi/types";
import {
  deleteThread,
  getMessagesFromThread,
  postMessageToCompletion,
  postMessageToResponse,
} from "../../../services/api/instances/responseApi/api";
import type { OutputMessageType } from "../../../services/api/instances/responseApi/types/common";
import type { TResponseApiRequestBody } from "../../../services/api/instances/responseApi/types/request";
import { getMessages } from "../../../services/api/methods";
import type { MessageList } from "../../../services/api/methodsTypes";
import type { RootState } from "../../../services/redux/types";
import {
  mergedDialogMessages,
  transformMessageListToChatCompletions,
  transformMessagesObject,
} from "../helpers";
import { clearMessages, setDialogValue, updateDialogField } from "./slice";
import { getText } from "../../../services/api/instances/assistantsApi/methods";
import { generateId } from "../../../shared/utils/generateId";
import { getError } from "../../../services/lib/getError";

export const startDialogAction = createAsyncThunk(
  "dialogs/startDialog",
  async (message: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { dialogValue, messages } = (getState() as RootState).dialogsState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .dialogs;
      if (!selectedId) return;
      const selectedObject = data.entities[selectedId];
      if (!dialogValue || !dialogValue.dialog_object) return;
      const { model, assistant_ref, thread_ref } = dialogValue.dialog_object;
      const threadId = thread_ref ? thread_ref : `thread_${generateId()}`;
      const postObject: TResponseApiRequestBody = {
        input: message,
        model: assistant_ref ? null : model,
        metadata: {
          assistant_ref: assistant_ref ? assistant_ref : null,
          thread_ref: threadId,
        },
      };
      const systemResponse = await postMessageToResponse(postObject);
      const systemMessage = systemResponse?.output.at(-1) as OutputMessageType;

      const newMessages: ChatCompletionsMessageObject[] = [
        ...messages,
        {
          role: "assistant",
          content: systemMessage.content?.at(-1)?.text as string,
        },
      ];

      if (!thread_ref) {
        const updatedValue: DialogValue = {
          dialog_object: {
            ...dialogValue.dialog_object,
            thread_ref: threadId,
          },
        };

        dispatch(
          editStorageObject({
            id: selectedId,
            name: selectedObject?.friendly_name,
            value: updatedValue,
            service: "dialogs",
          }),
        );
        dispatch(setDialogValue(updatedValue));
      }

      return newMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const startDialogWithCompletionsAction = createAsyncThunk(
  "dialogs/startDialogWithCompletions",
  async (message: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { dialogValue, selectedAssistant } = (getState() as RootState)
        .dialogsState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .dialogs;
      if (!selectedId) return;
      const selectedObject = data.entities[selectedId];
      if (!dialogValue || !dialogValue.dialog_object) return;
      const { model, assistant_ref, thread_ref, messages } =
        dialogValue.dialog_object;
      const currentModel = assistant_ref
        ? selectedAssistant?.value?.assistant_object?.model
        : model;
      const postObject: TChatCompletionRequest = {
        model: currentModel ?? null,
        messages: [{ role: "user", content: message }],
        metadata: {
          assistant_ref: assistant_ref,
          thread_ref: thread_ref,
        },
      };
      const systemResponse = await postMessageToCompletion(postObject);
      const systemMessage = systemResponse?.choices.at(-1);

      const updatedValue: DialogValue = {
        dialog_object: {
          ...dialogValue.dialog_object,
          messages: [
            ...dialogValue.dialog_object.messages,
            {
              role: "assistant",
              content: systemMessage?.message.content as string,
            },
          ],
        },
      };

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "dialogs",
        }),
      );

      return updatedValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getDialogMessagesAction = createAsyncThunk(
  "dialogs/getDialogMessages",
  async (threadRef: string, { getState, rejectWithValue }) => {
    try {
      const { dialogValue } = (getState() as RootState).dialogsState;
      if (!dialogValue?.dialog_object) return;
      const oldMessages = dialogValue?.dialog_object?.messages ?? [];
      const messages = await getMessagesFromThread(threadRef);
      const receivedMessages = transformMessagesObject(messages);
      const newMessages = mergedDialogMessages(oldMessages, receivedMessages);
      return newMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteDialogThreadAction = createAsyncThunk(
  "dialogs/deleteDialogThread",
  async (thread_ref: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { dialogValue } = (getState() as RootState).dialogsState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .dialogs;
      if (!selectedId || !dialogValue?.dialog_object) return;
      const selectedObject = data.entities[selectedId];
      if (!selectedObject) return;
      dispatch(clearMessages());
      dispatch(updateDialogField({ thread_ref: "", messages: [] }));

      const updatedValue: DialogValue = {
        dialog_object: {
          ...dialogValue?.dialog_object,
          thread_ref: "",
          messages: [],
        },
      };

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "dialogs",
        }),
      );

      return await deleteThread(thread_ref);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const migrationToNewApiAction = createAsyncThunk(
  "dialogs/migrationToNewApi",
  async (
    { value, id }: { value: DialogValue; id: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      if (value.dialog_object || !value.thread_id) return;
      const { data } = (getState() as RootState).storageObjectState.dialogs;
      const selectedObject = data.entities[id];
      const messages: MessageList = await getMessages(value.thread_id);

      const newMessages: ChatCompletionsMessageObject[] =
        transformMessageListToChatCompletions(messages);

      const newValue: DialogValue = {
        dialog_object: {
          thread_ref: `thread_${generateId()}`,
          assistant_ref: null,
          model: "gpt-4o",
          messages: newMessages,
        },
      };

      const postObject: TChatCompletionRequest = {
        model: "gpt-4o",
        messages: [...newMessages],
        metadata: {
          assistant_ref: null,
          thread_ref: newValue?.dialog_object?.thread_ref as string,
        },
      };

      dispatch(
        editStorageObject({
          id: id,
          name: selectedObject?.friendly_name,
          value: newValue,
          service: "dialogs",
        }),
      );

      await postMessageToCompletion(postObject);

      return newValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const uploadFileToDialogAction = createAsyncThunk(
  "dialogs/uploadFileToDialog",
  async (file: File, { signal, rejectWithValue }) => {
    try {
      return await getText(file, signal);
    } catch (e) {
      if (
        signal.aborted ||
        axios.isCancel(e) ||
        (e instanceof Error && e.name === "AbortError")
      ) {
        return rejectWithValue("Aborted");
      }
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
